/**
 * newDashboardData.js
 * Static demo data and chart configuration for the new dashboard.
 *
 * COLOR POLICY: Every single color value MUST reference tokens from colors.js.
 * Transparent variants are derived using hexToRgba() from base tokens.
 * CSS custom properties (--nd-*) bridge JS tokens → CSS module.
 */

import {
  BRAND,
  PRIMARY,
  HELP,
  SUCCESS,
  WARNING,
  PURPLE,
  ERROR,
  GREY,
  BORDER,
  TEAL,
  INDIGO,
  BASE,
} from "../../utils/constants/colors";
import { SHADOW } from "../../utils/constants/UI";

// ── UTILITY ─────────────────────────────────────────────
/** Derives rgba() from a hex token + alpha. All transparent colors must use this. */
export const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ── CHART DEFAULTS (all colors from tokens) ─────────────
export const CHART_GRID_COLOR = hexToRgba(BASE.BLACK, 0.04);

export const CHART_DEFAULTS = {
  font: { family: "'Inter'", size: 11 },
  color: GREY.TERTIARY,
  legend: {
    usePointStyle: true,
    pointStyle: "circle",
    boxWidth: 8,
    boxHeight: 8,
    padding: 20,
    color: GREY.SECONDARY,
  },
  tooltip: {
    backgroundColor: BASE.WHITE,
    titleColor: GREY.PRIMARY,
    bodyColor: GREY.SECONDARY,
    borderColor: BORDER.LIGHT,
    borderWidth: 1,
    cornerRadius: 10,
    padding: 12,
  },
};

// ── CHART COLORS - ReachIQ brand-aligned palette ────────
// Primary series: brand red → indigo → success green → warning gold → purple
export const CHART_COLORS = {
  brand: BRAND.PRIMARY,          // #FF4848 - ReachIQ red (primary series)
  brandSoft: PRIMARY.W_400,      // #FF6D6D - lighter red for fills/areas
  green: SUCCESS.W_500,          // #01A63E
  blue: INDIGO.W_500,            // #6774F4
  purple: PURPLE.W_500,          // #9162FA
  gold: WARNING.W_500,           // #F4A118
  error: ERROR.W_500,            // #E73F3F
  border: BORDER.DEFAULT,
  borderLight: BORDER.LIGHT,
  lightGrey: BASE.BACKGROUND_LIGHT,
};

// ═══════════════════════════════════════════════════════════
// CSS CUSTOM PROPERTIES MAP
// Spread onto root container via inline style.
// CSS module references these as var(--nd-*).
// ═══════════════════════════════════════════════════════════
export const CSS_VARS = {
  // ── Backgrounds ──
  "--nd-bg": BASE.BACKGROUND_LIGHT,
  "--nd-bg-white": BASE.WHITE,
  "--nd-transparent": BASE.TRANSPARENT,
  "--nd-scrollbar-thumb": hexToRgba(BASE.BLACK, 0.1),

  // ── Accent (primary brand) ──
  "--nd-accent": BRAND.PRIMARY,
  "--nd-accent-soft": PRIMARY.W_400,
  "--nd-accent-bg": hexToRgba(BRAND.PRIMARY, 0.08),
  "--nd-accent-bg-strong": hexToRgba(BRAND.PRIMARY, 0.12),
  "--nd-accent-border": hexToRgba(BRAND.PRIMARY, 0.22),
  "--nd-accent-shadow": `0 3px 14px ${hexToRgba(BRAND.PRIMARY, 0.25)}`,
  "--nd-accent-shadow-hover": `0 5px 20px ${hexToRgba(BRAND.PRIMARY, 0.3)}`,

  // ── Semantic colors ──
  "--nd-green": SUCCESS.W_500,
  "--nd-green-bg": hexToRgba(SUCCESS.W_500, 0.07),
  "--nd-green-border": hexToRgba(SUCCESS.W_500, 0.15),

  "--nd-blue": HELP.W_500,
  "--nd-blue-bg": hexToRgba(HELP.W_500, 0.06),

  "--nd-purple": PURPLE.W_600,
  "--nd-purple-bg": hexToRgba(PURPLE.W_600, 0.06),

  "--nd-teal": TEAL.W_500,
  "--nd-teal-bg": hexToRgba(TEAL.W_500, 0.06),

  "--nd-gold": WARNING.W_600,
  "--nd-gold-bg": hexToRgba(WARNING.W_600, 0.11),
  "--nd-gold-light": WARNING.W_400,
  "--nd-gold-shadow": `0 2px 8px ${hexToRgba(WARNING.W_400, 0.3)}`,

  "--nd-red": ERROR.W_500,
  "--nd-red-bg": hexToRgba(ERROR.W_500, 0.06),

  // ── Text ──
  "--nd-text": GREY.PRIMARY,
  "--nd-text-secondary": GREY.SECONDARY,
  "--nd-text-muted": GREY.TERTIARY,
  "--nd-text-dark": BRAND.SECONDARY,

  // ── Borders ──
  "--nd-border": BORDER.LIGHT,
  "--nd-border-hover": BORDER.HOVER,

  // ── Shadows ──
  "--nd-shadow-sm": SHADOW.xs,
  "--nd-shadow-md": SHADOW.md,
  "--nd-shadow-lg": SHADOW.lg,
  "--nd-shadow-xl": SHADOW.xl,

  // ── New Brand Colors ──
  "--nd-brand-red": BRAND.PRIMARY,
  "--nd-brand-light": PRIMARY.W_50,
  "--nd-brand-glow": hexToRgba(PRIMARY.W_50, 0.4),
  "--nd-success-text": SUCCESS.W_500,
  "--nd-success-bg": SUCCESS.W_50,
  "--nd-icon-shadow": hexToRgba(BRAND.PRIMARY, 0.25),

  // ═══════════════════════════════════════════════════════
  // HERO CARD SYSTEM - Clean white cards, brand-aligned accents
  // primary = brand red | green = success | blue = indigo | indigo = purple
  // ═══════════════════════════════════════════════════════
  "--nd-hero-primary-bg": BASE.WHITE,
  "--nd-hero-green-bg": BASE.WHITE,
  "--nd-hero-blue-bg": BASE.WHITE,

  "--nd-hero-primary-shadow": `0 1px 3px ${hexToRgba(BASE.BLACK, 0.06)}, 0 1px 2px ${hexToRgba(BASE.BLACK, 0.04)}`,
  "--nd-hero-green-shadow": `0 1px 3px ${hexToRgba(BASE.BLACK, 0.06)}, 0 1px 2px ${hexToRgba(BASE.BLACK, 0.04)}`,
  "--nd-hero-blue-shadow": `0 1px 3px ${hexToRgba(BASE.BLACK, 0.06)}, 0 1px 2px ${hexToRgba(BASE.BLACK, 0.04)}`,

  "--nd-hero-primary-shadow-hover": `0 4px 12px ${hexToRgba(BASE.BLACK, 0.08)}, 0 2px 4px ${hexToRgba(BASE.BLACK, 0.04)}`,
  "--nd-hero-green-shadow-hover": `0 4px 12px ${hexToRgba(BASE.BLACK, 0.08)}, 0 2px 4px ${hexToRgba(BASE.BLACK, 0.04)}`,
  "--nd-hero-blue-shadow-hover": `0 4px 12px ${hexToRgba(BASE.BLACK, 0.08)}, 0 2px 4px ${hexToRgba(BASE.BLACK, 0.04)}`,

  // Hero accent colors - brand-consistent, no random teal/indigo/purple
  "--nd-hero-primary-accent": BRAND.PRIMARY,     // Meetings Booked → red
  "--nd-hero-green-accent": SUCCESS.W_500,        // Qualified Leads → green
  "--nd-hero-blue-accent": INDIGO.W_500,          // Total Outreach → indigo
  "--nd-hero-indigo-accent": PURPLE.W_500,        // Active Campaigns → purple

  // White overlays for hero decorative elements (orbs, badges)
  "--nd-white-10": hexToRgba(BASE.WHITE, 0.1),
  "--nd-white-15": hexToRgba(BASE.WHITE, 0.15),
  "--nd-white-20": hexToRgba(BASE.WHITE, 0.2),
  "--nd-white-25": hexToRgba(BASE.WHITE, 0.25),
  "--nd-white-70": hexToRgba(BASE.WHITE, 0.7),

  // Drag handle
  "--nd-drag-bg": hexToRgba(BASE.BLACK, 0.04),
  "--nd-drag-bg-hover": hexToRgba(BASE.BLACK, 0.08),
};

// ── TAB CONFIG ──────────────────────────────────────────
export const TAB_CONFIG = {
  overview: {
    title: "Overview",
    subtitle: "Meetings, pipeline, and outreach at a glance",
  },
  activity: {
    title: "SDR Activity",
    subtitle: "Your dedicated team's daily output",
  },
  campaigns: {
    title: "Campaigns",
    subtitle: "Messaging performance and engagement",
  },
};



// ═════════════════════════════════════════════════════════
// CHART DATA BUILDERS - All colors from tokens
// ═════════════════════════════════════════════════════════
/* OLD STATIC SIGNATURES:
   export const getMeetingsTrendConfig = () => ({ ... })
   export const getOutreachByChannelConfig = () => ({ ... })
   ... (Functions now accept 'data' parameter for live updates)
*/

export const getMeetingsTrendConfig = (data) => ({
  type: "line",
  data: {
    labels:
      data?.length > 0
        ? data.map((d) => d.week)
        : ["W1 Jan", "W2 Jan", "W3 Jan", "W4 Jan", "W1 Feb", "W2 Feb"],
    datasets: [
      {
        label: "Meetings",
        data:
          data?.length > 0 ? data.map((d) => d.count) : [8, 6, 10, 9, 12, 14],
        borderColor: CHART_COLORS.brand,
        backgroundColor: hexToRgba(CHART_COLORS.brand, 0.06),
        fill: true,
        tension: 0.45,
        pointRadius: 5,
        pointBackgroundColor: CHART_COLORS.brand,
        pointBorderColor: BASE.WHITE,
        pointBorderWidth: 2.5,
        borderWidth: 2.5,
      },
    ],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: { beginAtZero: true, grid: { color: CHART_GRID_COLOR } },
      x: { grid: { display: false } },
    },
  },
});

// options.emailOnly: render only the Email series (tenant-specific, see
// isEmailOnlyOutreachChartTenant). Calls/Tasks datasets are dropped before
// Chart.js sees them, so the legend and the stacked totals shrink with them.
export const getOutreachByChannelConfig = (data, options = {}) => ({
  type: "bar",
  data: {
    labels:
      data?.emails?.length > 0
        ? data.emails.map((d) => d.week)
        : ["W1 Jan", "W2 Jan", "W3 Jan", "W4 Jan", "W1 Feb", "W2 Feb"],
    datasets: [
      {
        label: "Email",
        data:
          data?.emails?.length > 0
            ? data.emails.map((d) => d.count)
            : [1400, 1350, 1500, 1420, 1560, 1600],
        backgroundColor: CHART_COLORS.blue,
        borderRadius: 0,
        borderSkipped: false,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
      {
        label: "Calls",
        data:
          data?.calls?.length > 0
            ? data.calls.map((d) => d.count)
            : [620, 650, 680, 670, 710, 730],
        backgroundColor: CHART_COLORS.gold,
        borderRadius: 0,
        borderSkipped: false,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
      {
        label: "Tasks",
        data:
          data?.tasks?.length > 0
            ? data.tasks.map((d) => d.count)
            : [180, 220, 190, 210, 230, 240],
        backgroundColor: CHART_COLORS.purple,
        borderRadius: 0,
        borderSkipped: false,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
    ].filter((ds) => !options.emailOnly || ds.label === "Email"),
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "end",
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: { stacked: true, grid: { color: CHART_GRID_COLOR } },
    },
  },
});

export const getMeetingsByChannelConfig = (data) => ({
  type: "doughnut",
  data: {
    labels: ["Email", "Calls", "Multi-touch"],
    datasets: [
      {
        data: data ? [data.email, data.call, data.multi_touch] : [28, 12, 7],
        backgroundColor: [
          CHART_COLORS.blue,
          CHART_COLORS.gold,
          CHART_COLORS.purple,
        ],
        borderWidth: 0,
        spacing: 4,
      },
    ],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        position: "top",
        align: "end",
      },
    },
  },
});

export const getMeetingsBySeniorityConfig = (data) => ({
  type: "bar",
  data: {
    labels: data
      ? Object.keys(data).filter((k) => k !== "Other" || data[k] > 0)
      : ["C-Suite", "VP", "Director", "Manager"],
    datasets: [
      {
        data: data
          ? Object.keys(data)
              .filter((k) => k !== "Other" || data[k] > 0)
              .map((k) => data[k])
          : [12, 18, 17, 0],
        backgroundColor: [
          CHART_COLORS.brand,
          CHART_COLORS.purple,
          CHART_COLORS.blue,
          CHART_COLORS.borderLight,
        ],
        borderRadius: 0,
        borderSkipped: false,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
    ],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { grid: { color: CHART_GRID_COLOR } },
      x: { grid: { display: false } },
    },
  },
});

export const getDailyActivityTrendConfig = () => ({
  type: "bar",
  data: {
    labels: [
      "Mon",
      "Tue",
      "Wed",
      "Thu",
      "Fri",
      "Mon",
      "Tue",
      "Wed",
      "Thu",
      "Fri",
    ],
    datasets: [
      {
        label: "Email",
        data: [280, 310, 295, 320, 300, 340, 310, 330, 350, 320],
        backgroundColor: CHART_COLORS.blue,
        borderRadius: 0,
        borderSkipped: false,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
      {
        label: "Calls",
        data: [140, 155, 148, 150, 147, 165, 150, 158, 162, 148],
        backgroundColor: CHART_COLORS.gold,
        borderRadius: 0,
        borderSkipped: false,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
    ],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "end",
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: { stacked: true, grid: { color: CHART_GRID_COLOR } },
    },
  },
});

export const getChannelMixConfig = (data) => ({
  type: "doughnut",
  data: {
    labels: ["Email", "Calls", "Multi-touch"],
    datasets: [
      {
        data: data
          ? [data.email, data.call, data.multi_touch]
          : [8240, 4243, 0],
        backgroundColor: [
          CHART_COLORS.blue,
          CHART_COLORS.gold,
          CHART_COLORS.purple,
        ],
        borderWidth: 0,
        spacing: 4,
      },
    ],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        position: "top",
        align: "end",
      },
    },
  },
});

export const getReplySentimentConfig = (data) => {
  const personaData = data?.by_persona;

  // If we have persona breakdown, use that. Otherwise use totals.
  const labels = personaData ? Object.keys(personaData) : ["Overall"];

  const positive = personaData
    ? Object.values(personaData).map((v) => v.positive)
    : [data?.totals?.positive || 0];

  const neutral = personaData
    ? Object.values(personaData).map((v) => v.neutral)
    : [data?.totals?.neutral || 0];

  const notInterested = personaData
    ? Object.values(personaData).map((v) => v.not_interested)
    : [data?.totals?.not_interested || 0];

  return {
    type: "bar",
    data: {
      labels: labels,
      datasets: [
        {
          label: "Positive",
          data: positive,
          backgroundColor: CHART_COLORS.green,
          borderRadius: 6,
          maxBarThickness: 45,
          barPercentage: 0.5,
          categoryPercentage: 0.5,
        },
        {
          label: "Neutral",
          data: neutral,
          backgroundColor: CHART_COLORS.blue,
          borderRadius: 6,
          maxBarThickness: 45,
          barPercentage: 0.5,
          categoryPercentage: 0.5,
        },
        {
          label: "Not Interested",
          data: notInterested,
          backgroundColor: hexToRgba(GREY.SECONDARY, 0.3),
          borderRadius: 6,
          maxBarThickness: 45,
          barPercentage: 0.5,
          categoryPercentage: 0.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          align: "end",
          labels: {
            ...CHART_DEFAULTS.legend,
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 6,
            padding: 15,
            font: { family: "'Inter'", size: 11, weight: "500" },
          },
        },
        tooltip: CHART_DEFAULTS.tooltip,
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { font: { family: "'Inter'", size: 11 } },
        },
        y: {
          stacked: true,
          grid: {
            color: CHART_GRID_COLOR,
            borderDash: [4, 4],
            drawBorder: false,
          },
          beginAtZero: true,
          ticks: { font: { family: "'Inter'", size: 11 } },
        },
      },
    },
  };
};

export const getPipelineSnapshotConfig = (data) => ({
  type: "bar",
  data: {
    labels: data?.labels || ["Leads", "Engaged", "Meeting", "Opportunity"],
    datasets: [
      {
        data: data?.data || [3480, 487, 124, 38],
        backgroundColor: [HELP.W_500, TEAL.W_500, WARNING.W_600, BRAND.PRIMARY],
        borderRadius: 0,
        borderSkipped: false,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
    ],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        grid: { color: CHART_GRID_COLOR },
        ticks: {
          callback: (value) => (value >= 1000 ? value / 1000 + "k" : value),
        },
      },
    },
  },
});

export const getReplyRateTrendConfig = (data) => {
  const trendData = data?.data || [];

  return {
    type: "line",
    data: {
      labels:
        trendData.length > 0
          ? trendData.map((d) => {
              // Format YYYY-MM-DD to something shorter like "Mar 10"
              const date = new Date(d.week);
              return date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });
            })
          : ["W1", "W2", "W3", "W4", "W5", "W6"],
      datasets: [
        {
          label: "Reply Rate",
          data:
            trendData.length > 0
              ? trendData.map((d) => d.reply_rate)
              : [3.8, 4.0, 4.2, 4.5, 4.6, 4.8],
          borderColor: CHART_COLORS.green,
          backgroundColor: hexToRgba(SUCCESS.W_500, 0.06),
          fill: true,
          tension: 0.45,
          pointRadius: 4,
          pointBackgroundColor: CHART_COLORS.green,
          pointBorderColor: BASE.WHITE,
          pointBorderWidth: 2,
          borderWidth: 2.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: CHART_DEFAULTS.tooltip,
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: CHART_GRID_COLOR },
          ticks: {
            callback: (v) => v + "%",
            font: CHART_DEFAULTS.font,
          },
        },
        x: {
          grid: { display: false },
          ticks: { font: CHART_DEFAULTS.font },
        },
      },
    },
  };
};
